import React, { useState, useEffect } from "react";
import { Row, Col, Alert, Spinner, Button } from "reactstrap";
import { push } from "connected-react-router";
import { useDispatch } from "react-redux";
import Widget from "../../../components/Widget/Widget";
import KPICard from "./KPICard";
import StockChart from "./StockChart";
import PermintaanTable from "./PermintaanTable";
import ExpiredAlert from "./ExpiredAlert";
import { STOCK_UNIT, API_TIMEOUT } from "../../../utils";
import axios from "axios";

/**
 * Dashboard untuk User Unit/PUSTU
 * Menampilkan informasi stok unit, permintaan, dan penggunaan untuk pasien
 */
export default function UnitDashboard({ currentUser }) {
  const dispatch = useDispatch();
  const [loading, setLoading] = useState(true);
  const [stockData, setStockData] = useState(null);
  const [summaryData, setSummaryData] = useState({
    totalStok: 0,
    totalObat: 0,
    stokReady: 0,
    stokExpired: 0,
    permintaanPending: 0,
    penggunaanBulanIni: 0,
  });
  const idLokasi = currentUser?.idLokasi;

  useEffect(() => {
    if (idLokasi) {
      fetchDashboardData();
    }
  }, [idLokasi]);

  const fetchDashboardData = async () => {
    setLoading(true);
    try {
      // Fetch stock data
      const stockResponse = await axios.get(`${STOCK_UNIT}${idLokasi}`, {
        timeout: API_TIMEOUT,
      });

      if (stockResponse.data.success) {
        const data = stockResponse.data.data;

        // Calculate summary
        const totalStok = data.reduce((sum, item) => sum + (item.stok || 0), 0);
        const totalObat = [...new Set(data.map((item) => item.id_dataObat))]
          .length;
        const stokReady = data.reduce(
          (sum, item) => sum + (item.stok_ready || 0),
          0
        );
        const stokExpired = data.reduce(
          (sum, item) => sum + (item.stok_expired || 0),
          0
        );

        setStockData(data);
        setSummaryData({
          totalStok,
          totalObat,
          stokReady,
          stokExpired,
          permintaanPending: 0, // TODO: Fetch from API
          penggunaanBulanIni: 0, // TODO: Fetch from API
        });
      }
    } catch (error) {
      console.error("Error fetching dashboard data:", error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="text-center p-5">
        <Spinner color="primary" />
        <p className="mt-3">Memuat data dashboard...</p>
      </div>
    );
  }

  return (
    <div>
      <Row className="gx-4 gy-4 mb-4">
        <Col xs={12}>
          <h2 className="page-title">
            Dashboard{" "}
            <span className="fw-semi-bold">{currentUser?.lokasi}</span>
          </h2>
        </Col>
      </Row>

      {/* Quick Actions */}
      <Row className="gx-4 gy-4 mb-4">
        <Col xs={12}>
          <Widget containerClass="shadow-sm border-0">
            <Row>
              <Col xs={12} sm={6} md={3}>
                <Button
                  color="primary"
                  block
                  onClick={() => dispatch(push("/template/permintaan/ajukan"))}
                >
                  <i className="eva eva-plus-circle mr-2" />
                  Ajukan Permintaan
                </Button>
              </Col>
              <Col xs={12} sm={6} md={3}>
                <Button
                  color="success"
                  block
                  onClick={() => dispatch(push("/template/stock/masuk"))}
                >
                  <i className="eva eva-arrow-down mr-2" />
                  Input Stock Masuk
                </Button>
              </Col>
              <Col xs={12} sm={6} md={3}>
                <Button
                  color="warning"
                  block
                  onClick={() => dispatch(push("/template/stock/keluar"))}
                >
                  <i className="eva eva-arrow-up mr-2" />
                  Input Stock Keluar
                </Button>
              </Col>
              <Col xs={12} sm={6} md={3}>
                <Button
                  color="info"
                  block
                  onClick={() => dispatch(push("/template/laporan/neraca"))}
                >
                  <i className="eva eva-file-text mr-2" />
                  Lihat Laporan
                </Button>
              </Col>
            </Row>
          </Widget>
        </Col>
      </Row>

      {/* KPI Cards */}
      <Row className="gx-4 gy-4 mb-4">
        <Col xs={12} sm={6} lg={3}>
          <KPICard
            title="Total Stok"
            value={summaryData.totalStok}
            subtitle={`${summaryData.totalObat} jenis obat`}
            icon="eva eva-package-outline"
            color="info"
            format="number"
          />
        </Col>
        <Col xs={12} sm={6} lg={3}>
          <KPICard
            title="Stok Ready"
            value={summaryData.stokReady}
            subtitle={`${
              summaryData.totalStok > 0
                ? (
                    (summaryData.stokReady / summaryData.totalStok) *
                    100
                  ).toFixed(1)
                : 0
            }% dari total`}
            icon="eva eva-checkmark-circle-2-outline"
            color="success"
            format="number"
          />
        </Col>
        <Col xs={12} sm={6} lg={3}>
          <KPICard
            title="Stok Expired"
            value={summaryData.stokExpired}
            subtitle={`${
              summaryData.totalStok > 0
                ? (
                    (summaryData.stokExpired / summaryData.totalStok) *
                    100
                  ).toFixed(1)
                : 0
            }% dari total`}
            icon="eva eva-alert-circle-outline"
            color="danger"
            format="number"
          />
        </Col>
        <Col xs={12} sm={6} lg={3}>
          <KPICard
            title="Penggunaan Bulan Ini"
            value={summaryData.penggunaanBulanIni}
            subtitle="Untuk pasien"
            icon="eva eva-people-outline"
            color="warning"
            format="number"
          />
        </Col>
      </Row>

      {/* Charts */}
      <Row className="gx-4 gy-4 mb-4">
        <Col xs={12} lg={8}>
          <Widget containerClass="h-100 shadow-sm border-0">
            <h5 className="mb-3">Trend Stok (6 Bulan Terakhir)</h5>
            <StockChart data={stockData} />
          </Widget>
        </Col>
        <Col xs={12} lg={4}>
          <Widget containerClass="h-100 shadow-sm border-0">
            <h5 className="mb-3">Penggunaan per Penyakit</h5>
            {/* TODO: Implement chart */}
            <p className="text-muted">Chart akan ditampilkan di sini</p>
          </Widget>
        </Col>
      </Row>

      {/* Tables */}
      <Row className="gx-4 gy-4 mb-4">
        <Col xs={12} lg={6}>
          <Widget containerClass="h-100 shadow-sm border-0">
            <h5 className="mb-3">Permintaan ke Puskesmas</h5>
            <PermintaanTable />
          </Widget>
        </Col>
        <Col xs={12} lg={6}>
          <Widget containerClass="h-100 shadow-sm border-0">
            <h5 className="mb-3">Obat Expired / Mendekati Expired</h5>
            <ExpiredAlert data={stockData} />
          </Widget>
        </Col>
      </Row>

      {/* Alerts */}
      {summaryData.stokExpired > 0 && (
        <Alert color="warning" className="mb-4">
          <strong>Peringatan!</strong> Terdapat {summaryData.stokExpired} item
          obat yang telah expired.
        </Alert>
      )}

      {summaryData.permintaanPending > 0 && (
        <Alert color="info" className="mb-4">
          <strong>Info!</strong> Anda memiliki {summaryData.permintaanPending}{" "}
          permintaan yang sedang menunggu respon.
        </Alert>
      )}
    </div>
  );
}
